import { useEffect, useMemo, useRef, useState } from "react";
import {
  BaseBotData,
  BlacklistedOperationHours,
  BotData,
  BotType,
  LongShort,
  OverrideFieldType,
  StrippedBotData,
  SymbolsMetadataManager,
} from "@shared/types";
import {
  BaseBotFormValues,
  BotFormSetters,
  BotFormType,
  BotFormValues,
  HedgingMode,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import {
  channelValue,
  DEFAULT_ADVANCED_SETTINGS_VALUE,
  DEFAULT_OVERRIDE_FIELDS,
  DEFAULT_SIGNALS_BOT_ADVANCED_SETTINGS_VALUE,
  DEFAULT_SIGNALS_BOT_STRATEGY,
  GRACE_TARGET_EXCHANGE_FEATURE_FLAG,
} from "@shared/features/bots/BotForm/common/constants";
import {
  getAreBotValuesAdvanced,
  getBotFormDefaultStopValue,
  getBotFormDefaultTakeProfitValue,
  getBotValues,
  getDefaultCheckedFields,
  setBotFormValues,
} from "@shared/features/bots/BotForm/common/utils";
import { isSymbol } from "@shared/types/utils";
import { makeNameUnique } from "@shared/features/bots/utils";
import { useUser } from "@shared/utils/hooks/misc";
import {
  AMOUNT_PERCENTAGE_BASE_FEATURE_FLAG,
  MARGIN_EXCHANGES,
} from "@shared/common/constants";
import { sortItemsByExchangeOrder } from "@shared/utils/misc";
import { useDefaultExchangeSymbol } from "@shared/features/bots/BotForm/common/hooks";

interface useBotFormValuesProps {
  botType: BotType;
  botFormType?: BotFormType;
  isChannelConfiguration?: boolean;
  isBacktesting?: boolean;
  symbolsMetadata?: SymbolsMetadataManager;
}

const useBotFormValues = ({
  botType,
  botFormType,
  isChannelConfiguration,
  isBacktesting,
  symbolsMetadata,
}: useBotFormValuesProps) => {
  const { user } = useUser();
  const hasAmountPercentageFeatureFlag = user?.hasFeatureFlag(
    AMOUNT_PERCENTAGE_BASE_FEATURE_FLAG
  );
  const isSignalsBot = botType === BotType.SIGNAL;
  const isHedgingBot = botType === BotType.HEDGING;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isTradingViewBot = botType === BotType.TRADING_VIEW;
  const useDefaultChannelValues = isSignalsBot && !isChannelConfiguration;
  const [name, setName] = useState<BaseBotFormValues["name"]>("");
  const [subGroup, setSubGroup] = useState<BaseBotFormValues["subGroup"]>();
  const [accounts, setAccounts] = useState<BaseBotFormValues["accounts"]>([]);
  const [exchanges, setExchanges] = useState<BaseBotFormValues["exchanges"]>();
  const [symbols, setSymbols] = useState<BaseBotFormValues["symbols"]>([]);
  const [includeNewSymbols, setIncludeNewSymbols] =
    useState<BaseBotFormValues["includeNewSymbols"]>(isSignalsBot);
  const [trailingStopData, setTrailingStopData] = useState<
    BaseBotFormValues["trailingStopData"]
  >(useDefaultChannelValues ? channelValue : undefined);
  const [subGroupBotConfiguration, setSubGroupBotConfiguration] =
    useState<BaseBotFormValues["subGroupBotConfiguration"]>();
  const [entryStrategy, setEntryStrategy] = useState<
    BaseBotFormValues["entryStrategy"]
  >(useDefaultChannelValues ? DEFAULT_SIGNALS_BOT_STRATEGY : undefined);
  const [takeProfitStrategy, setTakeProfitStrategy] = useState<
    BaseBotFormValues["takeProfitStrategy"]
  >(useDefaultChannelValues ? DEFAULT_SIGNALS_BOT_STRATEGY : undefined);
  const [defaultTakeProfit, setDefaultTakeProfit] = useState<
    BaseBotFormValues["defaultTakeProfit"]
  >(() => getBotFormDefaultTakeProfitValue(botType, isChannelConfiguration));
  const [defaultStop, setDefaultStop] = useState<
    BaseBotFormValues["defaultStop"]
  >(() => getBotFormDefaultStopValue(botType));
  const [amountType, setAmountType] =
    useState<BaseBotFormValues["amountType"]>();
  const [amountValue, setAmountValue] =
    useState<BaseBotFormValues["amountValue"]>();
  const [amountPercentageBaseType, setAmountPercentageBaseType] =
    useState<BaseBotFormValues["amountPercentageBaseType"]>();
  const [amountCustomBaseValue, setAmountCustomBaseValue] =
    useState<BaseBotFormValues["amountCustomBaseValue"]>();
  const [longShort, setLongShort] = useState<BaseBotFormValues["longShort"]>(
    isSignalsBot || isHedgingBot ? [LongShort.LONG, LongShort.SHORT] : undefined
  );
  const [marginType, setMarginType] =
    useState<BaseBotFormValues["marginType"]>();
  const [multiplier, setMultiplier] =
    useState<BaseBotFormValues["multiplier"]>();
  const [multiSymbolMarginData, setMultiSymbolMarginData] = useState<
    BaseBotFormValues["multiSymbolMarginData"]
  >(useDefaultChannelValues ? channelValue : undefined);
  const [entryGracePercent, setEntryGracePercent] =
    useState<BaseBotFormValues["entryGracePercent"]>();
  const [entryGraceBaseline, setEntryGraceBaseline] =
    useState<BaseBotFormValues["entryGraceBaseline"]>();
  const [takeProfitGracePercent, setTakeProfitGracePercent] =
    useState<BaseBotFormValues["takeProfitGracePercent"]>();
  const [entryGraceTargetExchange, setEntryGraceTargetExchange] =
    useState<BaseBotFormValues["entryGraceTargetExchange"]>();
  const [takeProfitGraceTargetExchange, setTakeProfitGraceTargetExchange] =
    useState<BaseBotFormValues["takeProfitGraceTargetExchange"]>();
  const [trailingEntry, setTrailingEntry] = useState<
    BaseBotFormValues["trailingEntry"]
  >(useDefaultChannelValues ? channelValue : undefined);
  const [trailingTakeProfit, setTrailingTakeProfit] = useState<
    BaseBotFormValues["trailingTakeProfit"]
  >(useDefaultChannelValues ? channelValue : undefined);
  const [
    trailingTakeProfitLastTargetOnly,
    setTrailingTakeProfitLastTargetOnly,
  ] = useState<BaseBotFormValues["trailingTakeProfitLastTargetOnly"]>(false);
  const [stopTimeout, setStopTimeout] =
    useState<BaseBotFormValues["stopTimeout"]>();
  const [stopType, setStopType] = useState<BaseBotFormValues["stopType"]>();
  const [stopLimitPriceReduction, setStopLimitPriceReduction] =
    useState<BaseBotFormValues["stopLimitPriceReduction"]>();
  const [
    numberOfContinuousStopsTillBotStop,
    setNumberOfContinuousStopsTillBotStop,
  ] = useState<BaseBotFormValues["numberOfContinuousStopsTillBotStop"]>(
    isSignalsBot || isTradingViewBot || isTradingViewSignalsBot || isHedgingBot
      ? undefined
      : "1"
  );
  const [cancelOnProfitBeforeEntry, setCancelOnProfitBeforeEntry] = useState<
    BaseBotFormValues["cancelOnProfitBeforeEntry"]
  >(useDefaultChannelValues ? channelValue : false);
  const [isLeverageExchanges, setIsLeverageExchanges] = useState<
    BaseBotFormValues["isLeverageExchanges"]
  >(!isChannelConfiguration);
  const [useSymbolFromAlert, setUseSymbolFromAlert] =
    useState<BaseBotFormValues["useSymbolFromAlert"]>(false);
  const [advancedSettings, setAdvancedSettings] = useState<
    BaseBotFormValues["advancedSettings"]
  >(
    isSignalsBot
      ? DEFAULT_SIGNALS_BOT_ADVANCED_SETTINGS_VALUE
      : DEFAULT_ADVANCED_SETTINGS_VALUE
  );
  const [gridsData, setGridsData] = useState<BaseBotFormValues["gridsData"]>();
  const [hedgingMode, setHedgingMode] = useState<
    BaseBotFormValues["hedgingMode"]
  >(isHedgingBot ? HedgingMode.RANGE : undefined);
  const [hedgingGridsData, setHedgingGridsData] =
    useState<BaseBotFormValues["hedgingGridsData"]>();
  const [skipWithoutStopLoss, setSkipWithoutStopLoss] =
    useState<BaseBotFormValues["skipWithoutStopLoss"]>(false);
  const [followFuturesAsSpot, setFollowFuturesAsSpot] = useState<
    BaseBotFormValues["followFuturesAsSpot"]
  >(
    botFormType === BotFormType.BULK_EDIT ||
      botFormType === BotFormType.BULK_CREATE
      ? undefined
      : false
  );
  const [alternativeUsdPairs, setAlternativeUsdPairs] =
    useState<BaseBotFormValues["alternativeUsdPairs"]>();
  const [initialized, setInitialized] = useState(
    !isBacktesting &&
      (botFormType === BotFormType.CREATE ||
        botFormType === BotFormType.BULK_CREATE)
  );
  const [checkedFields, setCheckedFields] = useState<Set<ToggleField>>(() =>
    getDefaultCheckedFields(botType, isChannelConfiguration)
  );
  const [overrideFields, setOverrideFields] = useState<Set<OverrideFieldType>>(
    DEFAULT_OVERRIDE_FIELDS
  );
  const [isAdvancedView, setIsAdvancedView] = useState(
    () =>
      ![BotType.GRID, BotType.SIGNAL].includes(botType) ||
      !!isChannelConfiguration ||
      botFormType === BotFormType.COPY_PUBLIC
  );
  const [fieldsToInitialize, setFieldsToInitialize] = useState<
    Set<keyof BaseBotFormValues>
  >(new Set());
  const [operationHoursEvents, setOperationHoursEvents] =
    useState<BlacklistedOperationHours[]>();
  const [keywordToSymbolMapping, setKeywordToSymbolMapping] =
    useState<BaseBotFormValues["keywordToSymbolMapping"]>();
  const [longHedgingRatio, setLongHedgingRatio] = useState<
    BaseBotFormValues["longHedgingRatio"]
  >(isHedgingBot ? 50 : undefined);
  const amountChangedRef = useRef(false);

  const account = useMemo(() => {
    if (isSymbol(accounts)) return accounts;
    const sortedAccounts = sortItemsByExchangeOrder(accounts);
    const selectableAccounts = isHedgingBot
      ? sortedAccounts?.filter((account) =>
          MARGIN_EXCHANGES.has(account.exchange)
        )
      : sortedAccounts;
    const symbol = isSymbol(symbols) ? undefined : symbols?.[0];
    const validAccountsForSymbol = selectableAccounts?.filter((account) =>
      symbolsMetadata?.isSymbolActiveInExchange(symbol, account.exchange)
    );
    return validAccountsForSymbol?.[0] ?? selectableAccounts?.[0];
  }, [accounts, symbolsMetadata, symbols, isHedgingBot]);

  const { getDefaultSymbol } = useDefaultExchangeSymbol();

  useEffect(() => {
    const hasFeatureFlag = subGroup?.feature_flags?.includes(
      GRACE_TARGET_EXCHANGE_FEATURE_FLAG
    );

    if (subGroup && !hasFeatureFlag) {
      setEntryGraceTargetExchange(undefined);
      setTakeProfitGraceTargetExchange(undefined);
    }
  }, [subGroup]);

  const setAccount = (account?: BaseBotFormValues["account"]) => {
    if (account) {
      if (isSymbol(account)) setAccounts(account);
      else setAccounts([account]);
    } else setAccounts([]);
  };

  const formSetters: BotFormSetters = {
    setName,
    setSubGroupBotConfiguration,
    setSubGroup,
    setAccount,
    setAccounts,
    setExchanges,
    setSymbols,
    setIncludeNewSymbols,
    setTrailingStopData,
    setEntryStrategy,
    setTakeProfitStrategy,
    setDefaultTakeProfit,
    setDefaultStop,
    setStopType,
    setStopLimitPriceReduction,
    setAmountType,
    setAmountValue,
    setAmountPercentageBaseType,
    setAmountCustomBaseValue,
    setLongShort,
    setMarginType,
    setMultiplier,
    setMultiSymbolMarginData,
    setEntryGracePercent,
    setEntryGraceBaseline,
    setTrailingEntry,
    setTrailingTakeProfit,
    setTrailingTakeProfitLastTargetOnly,
    setStopTimeout,
    setNumberOfContinuousStopsTillBotStop,
    setAdvancedSettings,
    setCancelOnProfitBeforeEntry,
    setGridsData,
    setHedgingMode,
    setHedgingGridsData,
    setInitialized,
    setCheckedFields,
    setIsLeverageExchanges,
    setUseSymbolFromAlert,
    setOverrideFields,
    setSkipWithoutStopLoss,
    setAlternativeUsdPairs,
    setFollowFuturesAsSpot,
    setTakeProfitGracePercent,
    setEntryGraceTargetExchange,
    setTakeProfitGraceTargetExchange,
    setOperationHoursEvents,
    setKeywordToSymbolMapping,
    setLongHedgingRatio,
  };

  const symbolsWithDefault = useMemo(() => {
    if (!isSymbol(useSymbolFromAlert) && useSymbolFromAlert) {
      let defaultSymbol: string | undefined;
      if (account && !isSymbol(account)) {
        defaultSymbol = getDefaultSymbol(account?.exchange);
      } else if (!isSymbol(exchanges) && exchanges?.length) {
        defaultSymbol = getDefaultSymbol(exchanges[0]);
      }
      return defaultSymbol ? [defaultSymbol] : [];
    }
    return symbols;
  }, [symbols, account, useSymbolFromAlert, getDefaultSymbol]);

  const formValues: BotFormValues = useMemo(
    () => ({
      name,
      subGroup,
      subGroupBotConfiguration,
      account,
      accounts,
      exchanges,
      symbols: symbolsWithDefault,
      includeNewSymbols,
      trailingStopData,
      entryStrategy,
      takeProfitStrategy,
      defaultTakeProfit,
      defaultStop,
      stopType,
      stopLimitPriceReduction,
      amountType,
      amountValue,
      amountPercentageBaseType,
      amountCustomBaseValue,
      longShort,
      marginType,
      multiplier,
      multiSymbolMarginData,
      entryGracePercent,
      entryGraceBaseline,
      trailingEntry,
      trailingTakeProfit,
      trailingTakeProfitLastTargetOnly,
      stopTimeout,
      numberOfContinuousStopsTillBotStop,
      advancedSettings,
      cancelOnProfitBeforeEntry,
      gridsData,
      hedgingMode,
      hedgingGridsData,
      initialized,
      checkedFields,
      isLeverageExchanges,
      useSymbolFromAlert,
      overrideFields,
      skipWithoutStopLoss,
      alternativeUsdPairs,
      followFuturesAsSpot,
      takeProfitGracePercent,
      entryGraceTargetExchange,
      takeProfitGraceTargetExchange,
      operationHoursEvents,
      keywordToSymbolMapping,
      longHedgingRatio,
    }),
    [
      name,
      subGroup,
      subGroupBotConfiguration,
      account,
      accounts,
      symbolsWithDefault,
      includeNewSymbols,
      trailingStopData,
      entryStrategy,
      takeProfitStrategy,
      defaultTakeProfit,
      defaultStop,
      stopType,
      stopLimitPriceReduction,
      amountType,
      amountValue,
      amountPercentageBaseType,
      amountCustomBaseValue,
      longShort,
      marginType,
      multiplier,
      multiSymbolMarginData,
      entryGracePercent,
      entryGraceBaseline,
      trailingEntry,
      trailingTakeProfit,
      trailingTakeProfitLastTargetOnly,
      stopTimeout,
      numberOfContinuousStopsTillBotStop,
      advancedSettings,
      cancelOnProfitBeforeEntry,
      gridsData,
      hedgingMode,
      hedgingGridsData,
      initialized,
      checkedFields,
      isLeverageExchanges,
      useSymbolFromAlert,
      overrideFields,
      skipWithoutStopLoss,
      alternativeUsdPairs,
      followFuturesAsSpot,
      takeProfitGracePercent,
      entryGraceTargetExchange,
      takeProfitGraceTargetExchange,
      operationHoursEvents,
      exchanges,
      keywordToSymbolMapping,
      longHedgingRatio,
    ]
  );

  const toggleFieldToInitialize = (
    field: keyof BaseBotFormValues,
    add: boolean
  ) => {
    setFieldsToInitialize((prevFields) => {
      const updatedFields = new Set(prevFields);
      if (add) {
        updatedFields.add(field);
      } else {
        updatedFields.delete(field);
      }
      return updatedFields;
    });
  };
  const addFieldToInitialize = (field: keyof BaseBotFormValues) =>
    toggleFieldToInitialize(field, true);
  const removeFieldToInitialize = (field: keyof BaseBotFormValues) =>
    toggleFieldToInitialize(field, false);

  const initializeFormValues = (
    bot: BotData | BaseBotData | StrippedBotData | undefined,
    botsNames?: Record<number, string>
  ) => {
    if (!bot || !("options" in bot)) return;
    if ("name" in bot) {
      let name = bot.name;
      if (botFormType === BotFormType.DUPLICATE) {
        if (name.includes("Copy")) {
          name = makeNameUnique(name, botsNames);
        } else {
          name += " - Copy";
        }
      }
      setName(name);
    }

    const [botValues, checkedFields, overrideFields] = getBotValues(
      botType,
      bot,
      {
        useDefaultOrderDefaultValue: true,
        getExchangeSymbols: symbolsMetadata?.getExchangeSymbols,
        getAccountActiveSymbols: symbolsMetadata?.getAccountActiveSymbols,
        hasAmountPercentageFeatureFlag,
      },
      isChannelConfiguration
    );
    const keysToIgnore: (keyof BaseBotFormValues)[] = [
      "account",
      "accounts",
      "name",
      "subGroup",
      "subGroupBotConfiguration",
      "multiSymbolMarginData",
      "keywordToSymbolMapping",
    ];
    setBotFormValues(formSetters, botValues, undefined, keysToIgnore);
    setCheckedFields(checkedFields);
    setOverrideFields(overrideFields);
    if (initialized) {
      addFieldToInitialize?.("entryStrategy");
      addFieldToInitialize?.("takeProfitStrategy");
      addFieldToInitialize?.("amountValue");
    } else {
      setInitialized(true);
    }
    if (
      getAreBotValuesAdvanced(
        botValues,
        bot,
        checkedFields,
        overrideFields,
        botType,
        isChannelConfiguration
      )
    ) {
      setIsAdvancedView(true);
    }
  };

  return {
    initializeFormValues,
    formSetters,
    formValues,
    isAdvancedView,
    setIsAdvancedView,
    fieldsToInitialize,
    addFieldToInitialize,
    removeFieldToInitialize,
    amountChangedRef,
  };
};

export default useBotFormValues;
